import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type {
  AircallAnbindungLeitungen, AircallLeitung, AircallLeitungsAnrufe, ApiClient, KundeListe, Unternehmen,
} from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBestaetigung } from "@kundeninfo/ui";
import { Chip, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../components/meHuellen.js";
import { LeitungModal, gueltigAbBefund, leitungText } from "../components/AircallLeitungModal.js";
import type { AircallLeitungClient } from "../components/AircallLeitungModal.js";
import { KundenListe } from "./KundenListe.js";
import { formatDatum, formatZeitpunkt } from "../format.js";
import { berlinerTag } from "./tickets/ticketFormat.js";
import "./AircallLeitungen.css";

/**
 * DIE LEITUNGEN IM DIENST-DETAIL (Spec „Nacharbeitszeit, Leitung und Kunde je Anruf", Fassung 1.3, V3, V5, F18–F21,
 * E306, E311): der Katalog, Schlechtestes oben — offene Leitungen (Anrufe ohne gültige Zuordnung) zuerst, dann „ohne
 * Leitung", freie, zugeordnete, inaktive zuunterst. Je offener Leitung „Anrufe zeigen", für ADMIN „Zuordnen …"
 * (Kundenauswahl, dann dasselbe Modal wie in der Kundenakte) und „Keinem Kunden …". Block `acl`.
 */

export type AircallLeitungenClient = AircallLeitungClient & Pick<ApiClient, "aircallLeitungen"
  | "aircallLeitungsAnrufe" | "aircallKeinemKunden" | "aircallAnbindungLeitungen" | "getCustomers">;

const ZUSTAND: Record<AircallLeitung["zustand"], { text: string; art: "fehler" | "warnung" | "ok" | "aus" }> = {
  OFFEN: { text: "offen", art: "fehler" },
  OHNE_LEITUNG: { text: "ohne Leitung", art: "warnung" },
  FREI: { text: "frei", art: "aus" },
  ZUGEORDNET: { text: "zugeordnet", art: "ok" },
  KEINEM_KUNDEN: { text: "keinem Kunden", art: "ok" },
  INAKTIV: { text: "inaktiv", art: "aus" },
};

export function AircallLeitungen(p: {
  client: AircallLeitungenClient; istAdmin: boolean; companies?: Unternehmen[]; onGeaendert: () => void;
}): ReactElement {
  const { client } = p;
  const [liste, setListe] = useState<AircallLeitung[] | null>(null);
  const [ab, setAb] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [anrufe, setAnrufe] = useState<{ leitung: AircallLeitung; seite: AircallLeitungsAnrufe } | null>(null);
  const [aktion, setAktion] = useState<{ art: "KUNDE" | "KEINEM"; leitung: AircallLeitung } | null>(null);
  const [zuordnen, setZuordnen] = useState<{ leitung: AircallLeitung; kunde: KundeListe;
                                             sicht: AircallAnbindungLeitungen } | null>(null);
  const [version, setVersion] = useState(0);
  const laden = client.aircallLeitungen;

  useEffect(() => {
    let abgebrochen = false;
    laden().then((s) => { if (!abgebrochen) { setListe(s.items); setAb(s.leitungsabrechnungAb); setFehler(null); } })
      .catch(() => { if (!abgebrochen) { setListe(null); setFehler("Die Leitungen konnten nicht geladen werden."); } });
    return () => { abgebrochen = true; };
  }, [laden, version]);

  function neu(): void { setVersion((v) => v + 1); setAnrufe(null); p.onGeaendert(); }

  /** 409 im Zuordnen-Modal: die Anbindung neu laden, das Modal arbeitet mit der frischen Version weiter. */
  function konflikt(customerId: string): void {
    client.aircallAnbindungLeitungen(customerId)
      .then((s) => setZuordnen((z) => (z === null || z.kunde.id !== customerId ? z : { ...z, sicht: s })))
      .catch(() => setFehler("Die Aircall-Anbindung des Kunden konnte nicht neu geladen werden."));
  }

  async function zeigeAnrufe(l: AircallLeitung): Promise<void> {
    try {
      setAnrufe({ leitung: l, seite: await client.aircallLeitungsAnrufe(l.leitungId ?? "ohne", { limit: 20 }) });
    } catch {
      setFehler("Die offenen Anrufe konnten nicht geladen werden.");
    }
  }

  return (
    <section className="acl" aria-label="Leitungen">
      <h3 className="acl__titel">Leitungen</h3>
      <p className="acl__leise">
        {ab === null ? "Noch keine Leitung zugeordnet — bis dahin ist nichts offen."
          : `Leitungsabrechnung ab ${formatDatum(ab)}: Anrufe ohne gültige Zuordnung stehen als offen oben.`}
      </p>
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {liste !== null && liste.length === 0 && <p className="acl__leise">Der Leitungskatalog ist leer.</p>}
      <ul className="acl__liste">
        {(liste ?? []).map((l) => (
          <li key={l.leitungId ?? "ohne"} className="acl__zeile">
            <span className="acl__name">{l.leitungId === null ? "Ohne Leitung" : leitungText(l)}</span>
            <Chip statusArt={ZUSTAND[l.zustand].art}>{ZUSTAND[l.zustand].text}</Chip>
            {l.offeneAnrufe > 0 && (
              <span className="acl__leise">
                {l.offeneAnrufe} offen · {formatZeitpunkt(l.aeltesterOffener!)} bis {formatZeitpunkt(l.juengsterOffener!)}
              </span>
            )}
            {l.heute !== null && (
              <span className="acl__leise">
                {l.heute.keinemKunden ? "keinem Kunden" : l.heute.kundeName ?? "einem anderen Kunden"} ab{" "}
                {formatDatum(l.heute.validFrom)}
              </span>
            )}
            <span className="acl__aktionen">
              {l.offeneAnrufe > 0 && (
                <KiKnopf name={`Offene Anrufe ${l.name ?? "ohne Leitung"} zeigen`}
                  onClick={() => { void zeigeAnrufe(l); }}>Anrufe zeigen</KiKnopf>
              )}
              {p.istAdmin && l.leitungId !== null && l.zustand === "OFFEN" && (
                <>
                  <KiKnopf art="primaer" name={`Leitung ${l.name ?? ""} zuordnen`}
                    onClick={() => setAktion({ art: "KUNDE", leitung: l })}>Zuordnen …</KiKnopf>
                  <KiKnopf name={`Leitung ${l.name ?? ""} keinem Kunden zuordnen`}
                    onClick={() => setAktion({ art: "KEINEM", leitung: l })}>Keinem Kunden …</KiKnopf>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
      {anrufe !== null && (
        <section className="acl__anrufe" aria-label="Offene Anrufe">
          <div className="acl__kopf">
            <h4 className="acl__titel">Offene Anrufe — {anrufe.leitung.name ?? "ohne Leitung"} ({anrufe.seite.total})</h4>
            <KiKnopf name="Offene Anrufe schließen" onClick={() => setAnrufe(null)}>Schließen</KiKnopf>
          </div>
          <ul className="acl__liste">
            {anrufe.seite.items.map((a, i) => (
              <li key={`${a.startedAt}-${i}`} className="acl__zeile">
                <span>{formatZeitpunkt(a.startedAt)}</span>
                <span>{a.direction === "INBOUND" ? "eingehend" : "ausgehend"}</span>
                <span>{a.agentName ?? "—"}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {aktion?.art === "KEINEM" && (
        <KeinemKundenModal client={client} leitung={aktion.leitung} onAbbrechen={() => setAktion(null)}
          onFertig={() => { setAktion(null); neu(); }} />
      )}
      {aktion?.art === "KUNDE" && (
        <KundeWaehlenModal client={client} leitung={aktion.leitung} onAbbrechen={() => setAktion(null)}
          {...(p.companies === undefined ? {} : { companies: p.companies })}
          onGewaehlt={(kunde, sicht) => { setAktion(null); setZuordnen({ leitung: aktion.leitung, kunde, sicht }); }} />
      )}
      {zuordnen !== null && (
        <LeitungModal client={client} customerId={zuordnen.kunde.id} kundeName={zuordnen.kunde.companyName}
          sicht={zuordnen.sicht} modus="ZUORDNEN" leitungId={zuordnen.leitung.leitungId!}
          onKonflikt={() => konflikt(zuordnen.kunde.id)}
          onAbbrechen={() => setZuordnen(null)} onFertig={() => { setZuordnen(null); neu(); }} />
      )}
    </section>
  );
}

/** „1 offener Anruf gilt" / „3 offene Anrufe gelten" — die Zahl der Frage von „Keinem Kunden …" (S4, R-A8). */
function offeneText(n: number, bisZu: boolean): string {
  const satz = n === 1 ? "1 offener Anruf gilt" : `${n} offene Anrufe gelten`;
  return bisZu ? `Bis zu ${satz}` : satz;
}

/**
 * „Keinem Kunden …": intern, stillgelegt, bewusst nicht abzurechnen (F12, F20). Der Server lässt es aus dem
 * Dienst-Detail nur für eine Leitung ohne Anbindung ab dem Tag zu (422 `ZIEL_UNZULAESSIG`); kein Anruf wechselt
 * dabei seinen Abrechnungskunden. Die Frage nennt deshalb die offenen Anrufe, die danach geklärt sind — „bis zu",
 * wenn der Tag nach dem ältesten offenen Anruf liegt. Vorgabe ist der Berliner Tag des ältesten offenen Anrufs (F13).
 */
function KeinemKundenModal(p: { client: AircallLeitungenClient; leitung: AircallLeitung; onAbbrechen: () => void;
                                onFertig: () => void }): ReactElement {
  const aeltester = p.leitung.aeltesterOffener === null ? null : berlinerTag(p.leitung.aeltesterOffener);
  const ausgang = aeltester ?? "";
  const [ab, setAb] = useState(ausgang);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const { bestaetigen } = useBestaetigung();

  async function speichern(): Promise<void> {
    if (laeuft || p.leitung.leitungId === null) return;
    const befund = gueltigAbBefund(ab);
    if (befund !== null) { setFehler(befund); return; }
    const n = p.leitung.offeneAnrufe;
    const tag = formatDatum(ab);
    const zahl = n === 0 ? "" : ` ${offeneText(n, aeltester === null || ab > aeltester)} ab ${tag} als geklärt und `
      + "werden nicht abgerechnet.";
    if (!(await bestaetigen({ titel: "Keinem Kunden zuordnen?", bestaetigenText: "Keinem Kunden",
      text: `Leitung ${p.leitung.name ?? ""} ab ${tag} keinem Kunden zuordnen?${zahl}` }))) return;
    setLaeuft(true);
    setFehler(null);
    setMeldung(null);
    try {
      await p.client.aircallKeinemKunden(p.leitung.leitungId, { validFrom: ab, note: null });
      p.onFertig();
    } catch (e) {
      const amFeld = e instanceof ApiError ? e.fields?.find((f) => f.field === "validFrom") : undefined;
      if (amFeld !== undefined) setFehler(amFeld.message);
      else setMeldung(e instanceof ApiError ? e.message
        : "Die Leitung konnte nicht als „keinem Kunden“ eingetragen werden.");
    } finally {
      setLaeuft(false);
    }
  }

  async function abbrechen(): Promise<void> {
    if (laeuft) return;
    if (await darfAbbrechen(ab !== ausgang)) p.onAbbrechen();
  }

  return (
    <Modal titel={`${p.leitung.name ?? "Leitung"} keinem Kunden zuordnen`} abbrechenGesperrt={laeuft}
      onAbbrechen={() => { void abbrechen(); }}>
      {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
      <Formular beschriftung="oben">
        <Feld id="acl-ab" label="Ab" typ="date" bearbeitbar wert={ab} gesperrt={laeuft}
          onChange={(w) => { setAb(w); setFehler(null); }} {...(fehler === null ? {} : { fehler })} />
      </Formular>
      <ModalFuss speichernText="Keinem Kunden" speichernGesperrt={laeuft || ab === ""} abbrechenGesperrt={laeuft}
        onSpeichern={() => { void speichern(); }} onAbbrechen={() => { void abbrechen(); }} />
    </Modal>
  );
}

/** Schritt 1 von „Zuordnen …" aus der Klärliste: den Kunden wählen, dessen Anbindung die Leitung bekommt. */
function KundeWaehlenModal(p: {
  client: AircallLeitungenClient; leitung: AircallLeitung; companies?: Unternehmen[]; onAbbrechen: () => void;
  onGewaehlt: (kunde: KundeListe, sicht: AircallAnbindungLeitungen) => void;
}): ReactElement {
  const [suche, setSuche] = useState("");
  const [treffer, setTreffer] = useState<KundeListe[]>([]);
  const [laedt, setLaedt] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const getCustomers = p.client.getCustomers;

  useEffect(() => {
    let abgebrochen = false;
    setLaedt(true);
    getCustomers({ ...(suche === "" ? {} : { search: suche }), limit: 20 })
      .then(({ items }) => { if (!abgebrochen) setTreffer(items); })
      .catch(() => { if (!abgebrochen) setTreffer([]); })
      .finally(() => { if (!abgebrochen) setLaedt(false); });
    return () => { abgebrochen = true; };
  }, [getCustomers, suche]);

  async function waehle(id: string): Promise<void> {
    const kunde = treffer.find((k) => k.id === id);
    if (kunde === undefined) return;
    try {
      p.onGewaehlt(kunde, await p.client.aircallAnbindungLeitungen(kunde.id));
    } catch (e) {
      setMeldung(e instanceof ApiError && e.status === 404
        ? `Für ${kunde.companyName} ist keine Aircall-Anbindung hinterlegt — in der Kundenakte unter Stammdaten › `
          + "Aircall-Anbindung anlegen."
        : "Die Anbindung des Kunden konnte nicht geladen werden.");
    }
  }

  return (
    <Modal titel={`Leitung ${p.leitung.name ?? ""} zuordnen`} breite="breit" onAbbrechen={p.onAbbrechen}>
      {meldung !== null && <Hinweis art="warnung">{meldung}</Hinweis>}
      <KundenListe items={treffer} loading={laedt} error={null} search={suche} onSearch={setSuche}
        selectedId={null} onSelect={(id) => { void waehle(id); }}
        {...(p.companies === undefined ? {} : { companies: p.companies })} />
      <ModalFuss speichernText="Schließen" nurSchliessen onSpeichern={p.onAbbrechen} onAbbrechen={p.onAbbrechen} />
    </Modal>
  );
}
